import type {
    NavigationGuard,
    NavigationGuardReturn,
    RouteLocationNormalized,
    RouteRecordRaw,
    RouteRecordSingleView,
} from "vue-router";
import { ZodType, z } from "zod";

import { useAccess } from "@/composables/access";
import type { Permission, Resource, ResourceActions } from "@/services/PermissionsService";

import AdminRoutes from "../enums/routes/AdminRoutes";
import AppLayout from "../layout/AppLayout.vue";

const Id = z.coerce.number().positive();

function navigationGuardList(guard: undefined | NavigationGuard | NavigationGuard[]): NavigationGuard[] {
    if (typeof guard === "undefined") {
        return [];
    } else if (Array.isArray(guard)) {
        return guard;
    } else {
        return [guard];
    }
}

function validateIsPermitted(permission: Permission | Permission[]): NavigationGuardReturn {
    const permissions = Array.isArray(permission) ? permission : [permission];
    const permitted = permissions.some((permission) => {
        const [resource, action] = permission.split(/:/) as [Resource, ResourceActions<Resource>];
        const access = useAccess(resource);
        return access.permits(action);
    });
    if (!permitted) {
        return { name: "dashboard" };
    }
}

function indexRoute(permission: Permission | Permission[], routeRecord: RouteRecordSingleView): RouteRecordSingleView {
    return {
        ...routeRecord,
        beforeEnter: [...navigationGuardList(routeRecord.beforeEnter), () => validateIsPermitted(permission)],
    };
}

type DetailRouteOptions = {
    idType: ZodType;
};
function detailRoute(
    permission: Permission,
    routeRecord: RouteRecordSingleView,
    options: DetailRouteOptions = { idType: Id },
): RouteRecordSingleView {
    return {
        ...routeRecord,
        beforeEnter: [
            ...navigationGuardList(routeRecord.beforeEnter),
            (to: RouteLocationNormalized) => {
                const result = options.idType.safeParse(to.params.id);
                if (!result.success) {
                    return { name: "notfound" };
                }
            },
            () => validateIsPermitted(permission),
        ],
    };
}

export function adminRoutes(): RouteRecordRaw[] {
    return [
        {
            path: "/admin",
            component: AppLayout,
            children: [
                {
                    path: "/admin",
                    name: AdminRoutes.DASHBOARD,
                    component: () => import("../views/admin/DashboardView.vue"),
                },
                indexRoute("order:read", {
                    path: "/admin/orders",
                    name: AdminRoutes.ORDERS,
                    component: () => import("../views/admin/orders/IndexView.vue"),
                }),
                detailRoute(
                    "order:read",
                    {
                        path: "/admin/orders/:id",
                        name: AdminRoutes.ORDER,
                        component: () => import("../views/admin/orders/ShowView.vue"),
                    },
                    {
                        idType: z
                            .string()
                            .regex(/^VS\d{1,10}$/)
                            .or(Id),
                    },
                ),
                {
                    path: "/admin/orders/missing-items",
                    name: AdminRoutes.ORDERS_MISSING_ITEMS,
                    component: () => import("../views/admin/orders/OrdersMissingItemsView.vue"),
                },
                indexRoute("order:import", {
                    path: "/admin/orders/import",
                    name: AdminRoutes.ORDERS_IMPORT,
                    component: () => import("../views/admin/orders/ImportView.vue"),
                }),
                indexRoute("product:read", {
                    path: "/admin/products",
                    name: AdminRoutes.PRODUCTS,
                    component: () => import("../views/admin/products/IndexView.vue"),
                }),
                detailRoute(
                    "product:read",
                    {
                        path: "/admin/products/:id",
                        name: AdminRoutes.PRODUCT,
                        component: () => import("../views/admin/products/ShowView.vue"),
                    },
                    { idType: z.string().or(Id) },
                ),
                indexRoute("stats:day2day", {
                    path: "/admin/stats/day2day",
                    name: AdminRoutes.DAY2DAY,
                    component: () => import("../views/admin/stats/Day2Day.vue"),
                }),
                indexRoute("stats:aggregated_orders", {
                    path: "/admin/stats/aggregated-orders",
                    name: AdminRoutes.AGGREGATED_ORDERS,
                    component: () => import("../views/admin/stats/AggregatedOrders.vue"),
                }),
                indexRoute("stats:product_sales", {
                    path: "/admin/stats/product-sales",
                    name: AdminRoutes.PRODUCT_SALES,
                    component: () => import("../views/admin/stats/ProductSales.vue"),
                }),
                indexRoute("stats:sent_packages", {
                    path: "/admin/stats/sent-packages",
                    name: AdminRoutes.SENT_PACKAGES,
                    component: () => import("../views/admin/stats/SentPackages.vue"),
                }),
                indexRoute("stats:mixing_mixes", {
                    path: "/admin/stats/mixing-mixes",
                    name: AdminRoutes.MIXING_MIXES,
                    component: () => import("../views/admin/stats/MixingMixes.vue"),
                }),
                indexRoute("stats:mix_completion", {
                    path: "/admin/stats/mix-completion",
                    name: AdminRoutes.MIX_COMPLETION,
                    component: () => import("../views/admin/stats/MixCompletion.vue"),
                }),
                indexRoute("stats:logging", {
                    path: "/admin/stats/logging",
                    name: AdminRoutes.LOGGING,
                    component: () => import("../views/admin/stats/Logging.vue"),
                }),
                indexRoute("stats:waiting_orders", {
                    path: "/admin/stats/waiting-orders",
                    name: AdminRoutes.WAITING_ORDERS,
                    component: () => import("../views/admin/stats/WaitingOrders.vue"),
                }),
                indexRoute("stats:rewards", {
                    path: "/admin/stats/rewards",
                    name: AdminRoutes.REWARDS,
                    component: () => import("../views/admin/stats/Rewards.vue"),
                }),
                indexRoute("stats:ordered_mixes", {
                    path: "/admin/stats/ordered-mixes",
                    name: AdminRoutes.ORDERED_MIXES,
                    component: () => import("../views/admin/stats/OrderedMixes.vue"),
                }),
                indexRoute("stats:production_analysis", {
                    path: "/admin/stats/production-analysis",
                    name: AdminRoutes.PRODUCTION_ANALYSIS,
                    component: () => import("../views/admin/stats/ProductionAnalysis.vue"),
                }),
                indexRoute("stats:picking_items", {
                    path: "/admin/stats/picking",
                    name: AdminRoutes.PICKING_ITEMS,
                    component: () => import("../views/admin/stats/Picking.vue"),
                }),
                {
                    path: "/admin/stock-items",
                    name: AdminRoutes.STOCK_ITEMS,
                    component: () => import("../views/admin/stock_items/IndexView.vue"),
                },
                indexRoute("stock_item:read", {
                    path: "/admin/past-stock-quantities",
                    name: AdminRoutes.PAST_STOCK_QUANTITIES,
                    component: () => import("../views/admin/past_stock_quantities/IndexView.vue"),
                }),
                indexRoute("admin_user:read", {
                    path: "/admin/admin-users",
                    name: AdminRoutes.ADMIN_USERS,
                    component: () => import("../views/admin/admin_users/IndexView.vue"),
                }),
                detailRoute("admin_user:read", {
                    path: "/admin/admin-users/:id",
                    name: AdminRoutes.ADMIN_USER,
                    component: () => import("../views/admin/admin_users/ShowView.vue"),
                }),
                indexRoute("employee:read", {
                    path: "/admin/employees",
                    name: AdminRoutes.EMPLOYEES,
                    component: () => import("../views/admin/employees/IndexView.vue"),
                }),
                detailRoute("employee:read", {
                    path: "/admin/employees/:id",
                    name: AdminRoutes.EMPLOYEE,
                    component: () => import("../views/admin/employees/ShowView.vue"),
                }),
                indexRoute("employee:read", {
                    path: "/admin/employees/logged-in-users",
                    name: AdminRoutes.LOGGED_IN_USERS,
                    component: () => import("../views/admin/employees/LoggedInUsersView.vue"),
                }),
                indexRoute("job_lock:read", {
                    path: "/admin/job-locks",
                    name: AdminRoutes.JOB_LOCKS,
                    component: () => import("../views/admin/job_locks/IndexView.vue"),
                }),
                indexRoute("system_settings:edit", {
                    path: "/admin/system-settings",
                    name: AdminRoutes.SYSTEM_SETTINGS,
                    component: () => import("../views/admin/system_settings/IndexView.vue"),
                }),
                indexRoute("pick_container:read", {
                    path: "/admin/pick-containers",
                    name: AdminRoutes.PICKCONTAINERS,
                    component: () => import("../views/admin/pick_containers/IndexView.vue"),
                }),
                detailRoute("pick_container:read", {
                    path: "/admin/pick-containers/:id",
                    name: AdminRoutes.PICKCONTAINER,
                    component: () => import("../views/admin/pick_containers/ShowView.vue"),
                }),
                indexRoute("pick_order:read", {
                    path: "/admin/pick-orders",
                    name: AdminRoutes.PICKORDERS,
                    component: () => import("../views/admin/pick_orders/IndexView.vue"),
                }),
                indexRoute("order_group:read", {
                    path: "/admin/order-groups",
                    name: AdminRoutes.ORDER_GROUPS,
                    component: () => import("../views/admin/order_groups/IndexView.vue"),
                }),
                detailRoute("order_group:read", {
                    path: "/admin/order-groups/:id",
                    name: AdminRoutes.ORDER_GROUP,
                    component: () => import("../views/admin/order_groups/ShowView.vue"),
                }),
                detailRoute("cart:read", {
                    path: "/admin/cart/:id",
                    name: AdminRoutes.CART,
                    component: () => import("../views/admin/carts/ShowView.vue"),
                }),
                indexRoute("tablet:read", {
                    path: "/admin/tablets",
                    name: AdminRoutes.TABLETS,
                    component: () => import("../views/admin/tablets/IndexView.vue"),
                }),
                detailRoute("tablet:read", {
                    path: "/admin/tablets/:id",
                    name: AdminRoutes.TABLET,
                    component: () => import("../views/admin/tablets/ShowView.vue"),
                }),
                indexRoute("protocol:read", {
                    path: "/admin/protocols",
                    name: AdminRoutes.PROTOCOLS,
                    component: () => import("../views/admin/protocols/IndexView.vue"),
                }),
                detailRoute("protocol:read", {
                    path: "/admin/protocols/:id",
                    name: AdminRoutes.PROTOCOL,
                    component: () => import("../views/admin/protocols/ShowView.vue"),
                }),
                indexRoute("category:read", {
                    path: "/admin/categories",
                    name: AdminRoutes.CATEGORIES,
                    component: () => import("../views/admin/categories/IndexView.vue"),
                }),
                detailRoute("category:read", {
                    path: "/admin/categories/:id",
                    name: AdminRoutes.CATEGORY,
                    component: () => import("../views/admin/categories/ShowView.vue"),
                }),
                indexRoute("ingredient:read", {
                    path: "/admin/ingredients",
                    name: AdminRoutes.INGREDIENTS,
                    component: () => import("../views/admin/ingredients/IndexView.vue"),
                }),
                detailRoute(
                    "ingredient:read",
                    {
                        path: "/admin/ingredients/:id",
                        name: AdminRoutes.INGREDIENT,
                        component: () => import("../views/admin/ingredients/ShowView.vue"),
                    },
                    { idType: z.string().or(Id) },
                ),
                indexRoute("package:read", {
                    path: "/admin/packages",
                    name: AdminRoutes.PACKAGES,
                    component: () => import("../views/admin/packages/IndexView.vue"),
                }),
                detailRoute("package:read", {
                    path: "/admin/packages/:id",
                    name: AdminRoutes.PACKAGE,
                    component: () => import("../views/admin/packages/ShowView.vue"),
                }),
                indexRoute("ean:read", {
                    path: "/admin/eans",
                    name: AdminRoutes.EANS,
                    component: () => import("../views/admin/eans/IndexView.vue"),
                }),
                indexRoute("production_plan:read", {
                    path: "/admin/production-plans",
                    name: AdminRoutes.PRODUCTION_PLANS,
                    component: () => import("../views/admin/production_plans/IndexView.vue"),
                }),
                detailRoute("production_plan:read", {
                    path: "/admin/production-plans/:id",
                    name: AdminRoutes.PRODUCTION_PLAN,
                    component: () => import("../views/admin/production_plans/ShowView.vue"),
                }),
                indexRoute("operational_cost:read", {
                    path: "/admin/operational-costs",
                    name: AdminRoutes.OPERATIONAL_COSTS,
                    component: () => import("../views/admin/operational_costs/IndexView.vue"),
                }),
                detailRoute("operational_cost:read", {
                    path: "/admin/operational-costs/:id",
                    name: AdminRoutes.OPERATIONAL_COST,
                    component: () => import("../views/admin/operational_costs/ShowView.vue"),
                }),
                indexRoute("expedition_package:read", {
                    path: "/admin/expedition-packages",
                    name: AdminRoutes.EXPEDITION_PACKAGES,
                    component: () => import("../views/admin/expedion_packages/IndexView.vue"),
                }),
                indexRoute(["label:generate", "label:print_qr_label"], {
                    path: "/admin/labels",
                    name: AdminRoutes.LABELS,
                    component: () => import("../views/admin/labels/IndexView.vue"),
                }),
                indexRoute("package_code:read", {
                    path: "/admin/package-codes",
                    name: AdminRoutes.PACKAGE_CODES,
                    component: () => import("../views/admin/package_codes/IndexView.vue"),
                }),
                detailRoute("package_code:read", {
                    path: "/admin/package-codes/:id",
                    name: AdminRoutes.PACKAGE_CODE,
                    component: () => import("../views/admin/package_codes/ShowView.vue"),
                }),
                indexRoute("temperature_control:edit", {
                    path: "/admin/temperature-control",
                    name: AdminRoutes.TEMPERATURE_CONTROL,
                    component: () => import("../views/admin/temperature_control/IndexView.vue"),
                }),
                indexRoute("expedition_label_type:read", {
                    path: "/admin/expedition-label-types",
                    name: AdminRoutes.EXPEDITION_LABEL_TYPES,
                    component: () => import("../views/admin/expedition_label_types/IndexView.vue"),
                }),
                detailRoute("expedition_label_type:read", {
                    path: "/admin/expedition-label-types/:id",
                    name: AdminRoutes.EXPEDITION_LABEL_TYPE,
                    component: () => import("../views/admin/expedition_label_types/ShowView.vue"),
                }),
                indexRoute("prototype:read", {
                    path: "/admin/prototypes",
                    name: AdminRoutes.PROTOTYPES,
                    component: () => import("../views/admin/prototypes/IndexView.vue"),
                }),
                detailRoute("prototype:read", {
                    path: "/admin/prototypes/:id",
                    name: AdminRoutes.PROTOTYPE,
                    component: () => import("../views/admin/prototypes/ShowView.vue"),
                }),
                indexRoute("mixing:read", {
                    path: "/admin/tablet-mixes",
                    name: AdminRoutes.TABLET_MIXES,
                    component: () => import("../views/admin/tablet_mixes/IndexView.vue"),
                }),
                detailRoute("mixing:read", {
                    path: "/admin/tablet-mixes/:id",
                    name: AdminRoutes.TABLET_MIX,
                    component: () => import("../views/admin/tablet_mixes/ShowView.vue"),
                }),
                indexRoute("mix_completion:read", {
                    path: "/admin/mix-stocks",
                    name: AdminRoutes.MIX_STOCKS,
                    component: () => import("../views/admin/mix_stocks/IndexView.vue"),
                }),
                indexRoute("expedition_user:read", {
                    path: "/admin/expedition-users",
                    name: AdminRoutes.EXPEDITION_USERS,
                    component: () => import("../views/admin/expedition_users/IndexView.vue"),
                }),
                detailRoute("expedition_user:read", {
                    path: "/admin/expedition-users/:id",
                    name: AdminRoutes.EXPEDITION_USER,
                    component: () => import("../views/admin/expedition_users/ShowView.vue"),
                }),
                indexRoute("expedition_user_action:form", {
                    path: "/admin/expedition-user-actions",
                    name: AdminRoutes.EXPEDITION_USER_ACTIONS,
                    component: () => import("../views/admin/expedition_user_actions/IndexView.vue"),
                }),
                {
                    path: "/admin/carrier-handover-date",
                    name: AdminRoutes.CARRIER_HANDOVER_DATE,
                    component: () => import("@/views/admin/carrier_handover_estimates/IndexView.vue"),
                },
                indexRoute("delivery_estimate:read", {
                    path: "/admin/delivery-estimates",
                    name: AdminRoutes.DELIVERY_ESTIMATES,
                    component: () => import("@/views/admin/delivery_estimates/IndexView.vue"),
                }),
                indexRoute("role:read", {
                    path: "/admin/roles",
                    name: AdminRoutes.ROLES,
                    component: () => import("../views/admin/roles/IndexView.vue"),
                }),
                detailRoute("role:read", {
                    path: "/admin/roles/:id",
                    name: AdminRoutes.ROLE,
                    component: () => import("../views/admin/roles/ShowView.vue"),
                }),
                indexRoute("picking_settings:edit", {
                    path: "/admin/picking-settings",
                    name: AdminRoutes.PICKING_SETTINGS,
                    component: () => import("../views/admin/picking_settings/IndexView.vue"),
                }),
                {
                    path: "/admin/:pathMatch(.*)*",
                    component: () => import("../views/admin/NotFound.vue"),
                    name: "notfound",
                },
            ],
        },
    ];
}
